import type { operations } from '@molchanovs/api-contract';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@shared/api';
import type { StockSyncReceipt } from './stock-sync';

type PreviewWire =
  operations['getApiV1ShopifyStockSyncSharedGroupPreview']['responses'][200]['content']['application/json'];
const etagSchema = z.string().regex(/^"[^"\r\n]+"$/u);
const targetSchema = z.strictObject({
  inventoryItemId: z.string().regex(/^gid:\/\/shopify\/InventoryItem\/[1-9][0-9]*$/u),
  locationId: z.string().regex(/^gid:\/\/shopify\/Location\/[1-9][0-9]*$/u),
});
export type SharedStockTarget = z.infer<typeof targetSchema>;
export const sharedStockPreviewSchema: z.ZodType<PreviewWire> = z.object({
  data: z.object({
    version: z.literal(1),
    inventoryItemId: z.string(),
    locationId: z.string(),
    sourceProductId: z.string(),
    etag: etagSchema,
    eligible: z.boolean(),
    reason: z.enum(['member_blocked', 'cap_exceeded']).nullable(),
    members: z
      .array(
        z.object({
          inventoryItemId: z.string().nullable(),
          variantId: z.string(),
          desiredQuantity: z.number().int().nonnegative().nullable(),
          observedQuantity: z.number().int().nullable(),
          evidenceReference: z.string().nullable(),
          status: z.enum([
            'changed',
            'unchanged',
            'baseline_missing',
            'baseline_uncertain',
            'baseline_quarantined',
            'mapping_changed',
            'ineligible',
          ]),
          reason: z.string().nullable(),
        })
      )
      .min(2)
      .max(1000),
  }),
});
export type SharedStockPreview = PreviewWire['data'];

export async function getSharedStockPreview(
  target: SharedStockTarget,
  signal: AbortSignal
): Promise<SharedStockPreview> {
  const input = targetSchema.parse(target);
  const response = await api.getWithMetadata('/api/v1/shopify/stock-sync/shared-group-preview', {
    params: input,
    signal,
    schema: sharedStockPreviewSchema,
  });
  const preview = sharedStockPreviewSchema.parse(response.data).data;
  if (
    preview.inventoryItemId !== input.inventoryItemId ||
    preview.locationId !== input.locationId ||
    preview.etag !== etagSchema.parse(response.headers.get('etag'))
  ) {
    throw new Error('Invalid shared stock preview identity');
  }
  return preview;
}

export const sharedStockPreviewQuery = (viewerAccessScope: string, target: SharedStockTarget) =>
  queryOptions({
    queryKey: ['shopify-shared-stock-preview', viewerAccessScope, target],
    queryFn: ({ signal }) => getSharedStockPreview(target, signal),
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

export async function requestSharedStockRun(
  target: SharedStockTarget,
  etag: string,
  idempotencyKey: string
): Promise<StockSyncReceipt> {
  const body = targetSchema.parse(target);
  const response = z
    .object({
      data: z.object({
        id: z.uuid(),
        status: z.literal('pending'),
        acceptedAt: z.iso.datetime({ offset: true }),
        monitorUrl: z.string(),
      }),
    })
    .parse(
      await api.post('/api/v1/shopify/stock-sync/shared-group-runs', body, {
        headers: {
          'If-Match': etagSchema.parse(etag),
          'Idempotency-Key': z.string().trim().min(1).max(200).parse(idempotencyKey),
        },
      })
    ).data;
  if (response.monitorUrl !== `/api/v1/shopify/stock-sync/runs/${response.id}`)
    throw new Error('Invalid stock synchronization monitor');
  return response;
}
